import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  PackagePlus, CheckCircle2, XCircle, Clock4, Store, User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRestockRequests() {
  const { getAuthHeaders } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [resolveDialog, setResolveDialog] = useState({ open: false, request: null, mode: 'approve' });
  const [approvedQty, setApprovedQty] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? `${API}/zones/restock-requests`
        : `${API}/zones/restock-requests?status=${filter}`;
      const res = await axios.get(url, getAuthHeaders());
      setRequests(res.data);
    } catch (e) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeaders]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openResolve = (request, mode) => {
    setResolveDialog({ open: true, request, mode });
    setApprovedQty(request.requested_quantity);
    setNotes('');
  };

  const submitResolve = async () => {
    if (!resolveDialog.request) return;
    setSubmitting(true);
    try {
      const url = `${API}/zones/restock-requests/${resolveDialog.request.id}/${resolveDialog.mode}`;
      const payload = resolveDialog.mode === 'approve'
        ? { approved_quantity: Number(approvedQty), notes }
        : { notes };
      await axios.put(url, payload, getAuthHeaders());
      toast.success(resolveDialog.mode === 'approve' ? 'Solicitud aprobada y stock transferido' : 'Solicitud rechazada');
      setResolveDialog({ open: false, request: null, mode: 'approve' });
      fetchRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al procesar');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'approved') return { text: 'Aprobada', cls: 'bg-green-500/10 text-green-400' };
    if (status === 'rejected') return { text: 'Rechazada', cls: 'bg-red-500/10 text-red-400' };
    return { text: 'Pendiente', cls: 'bg-blue-500/10 text-blue-400' };
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <PackagePlus className="w-6 h-6 text-gold" />
              Solicitudes de Reposición
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Gestiona las solicitudes de inventario de los gerentes de zona</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1 bg-zinc-900/50 border border-zinc-800 rounded-sm p-1 w-fit" data-testid="filter-tabs">
          {[
            { id: 'pending', label: 'Pendientes', icon: Clock4 },
            { id: 'approved', label: 'Aprobadas', icon: CheckCircle2 },
            { id: 'rejected', label: 'Rechazadas', icon: XCircle },
            { id: 'all', label: 'Todas', icon: PackagePlus },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm transition-colors ${
                filter === tab.id ? 'bg-gold/20 text-gold' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
            <p className="text-zinc-500">Cargando...</p>
          </Card>
        ) : requests.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-12 text-center" data-testid="empty-requests">
            <PackagePlus className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400">No hay solicitudes en este filtro</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => {
              const badge = statusBadge(r.status);
              return (
                <Card key={r.id} className="bg-zinc-900/50 border-zinc-800 p-5" data-testid={`request-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-white font-semibold">{r.product_name}</h3>
                        <span className="text-zinc-500">× {r.requested_quantity}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${badge.cls}`}>{badge.text}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-zinc-500">
                        <span className="flex items-center gap-1"><Store className="w-3 h-3" /> {r.branch_name}</span>
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {r.requested_by_name}</span>
                        <span>{new Date(r.created_at).toLocaleString('es-MX')}</span>
                      </div>
                      {r.notes && (
                        <p className="text-xs text-zinc-400 italic">"{r.notes}"</p>
                      )}
                      {r.status !== 'pending' && (
                        <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">
                          <p>Resuelto por: <span className="text-zinc-300">{r.resolved_by_name}</span></p>
                          {r.approved_quantity != null && (
                            <p>Cantidad aprobada: <span className="text-green-400">{r.approved_quantity}</span></p>
                          )}
                          {r.resolution_notes && <p className="italic">"{r.resolution_notes}"</p>}
                        </div>
                      )}
                    </div>

                    {r.status === 'pending' && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => openResolve(r, 'approve')}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-btn-${r.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openResolve(r, 'reject')}
                          className="border-red-700 text-red-400 hover:bg-red-900/30"
                          data-testid={`reject-btn-${r.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rechazar
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialog.open} onOpenChange={(o) => !o && setResolveDialog({ open: false, request: null, mode: 'approve' })}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {resolveDialog.mode === 'approve' ? 'Aprobar Solicitud' : 'Rechazar Solicitud'}
            </DialogTitle>
          </DialogHeader>
          {resolveDialog.request && (
            <div className="space-y-4 py-2">
              <div className="bg-zinc-800/50 rounded-sm p-3">
                <p className="text-sm text-white">{resolveDialog.request.product_name}</p>
                <p className="text-xs text-zinc-500">{resolveDialog.request.branch_name}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Solicitado: <span className="text-white">{resolveDialog.request.requested_quantity}</span> · 
                  por <span className="text-zinc-300">{resolveDialog.request.requested_by_name}</span>
                </p>
              </div>
              {resolveDialog.mode === 'approve' && (
                <div>
                  <Label htmlFor="approved-qty" className="text-zinc-300">Cantidad a aprobar</Label>
                  <Input
                    id="approved-qty"
                    type="number"
                    min={1}
                    value={approvedQty}
                    onChange={(e) => setApprovedQty(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="approved-qty-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">El stock se descontará del inventario central y se sumará a la sucursal.</p>
                </div>
              )}
              <div>
                <Label htmlFor="resolve-notes" className="text-zinc-300">Notas (opcional)</Label>
                <Textarea
                  id="resolve-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="resolve-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveDialog({ open: false, request: null, mode: 'approve' })} data-testid="resolve-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={submitResolve}
              disabled={submitting}
              className={resolveDialog.mode === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="resolve-submit-btn"
            >
              {submitting ? 'Procesando...' : resolveDialog.mode === 'approve' ? 'Aprobar y Transferir' : 'Rechazar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
